import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Slider from '@material-ui/core/Slider';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import TuneIcon from '@material-ui/icons/Tune';
import { DEFAULT_AI_SEARCH_ACCURACY } from 'constants/defaultAiSearchAccuracy';
import strings from 'strings';
import { TextBox, When } from 'UI/components/atoms';

import { ACCURACY_MARKS } from './AISearch.constants';
import { useAISearchStyles } from './AISearch.styled';

export const AISearch = ({
  onChange,
  name,
  title,
  placeholder,
  value,
  showAccuracySettings = true
}) => {
  const [accuracy, setAccuracy] = useState(DEFAULT_AI_SEARCH_ACCURACY);
  const [aiSummary, setAiSummary] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const isSettingsOpen = Boolean(anchorEl);
  const classes = useAISearchStyles({ isSettingsOpen });

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleOpenSettings = event => {
    setAnchorEl(event.currentTarget);
  };

  const emitChange = ({ accuracy: newAccuracy, aiSummary: newAiSummary }) => {
    if (!onChange) return;
    onChange(name, {
      accuracy: newAccuracy,
      aiSummary: newAiSummary
    });
  };

  const handleOnMouseUpSlider = newValue => {
    setAccuracy(newValue);
    emitChange({ accuracy: newValue, aiSummary });
  };

  const handleAiSummaryChange = (_, newValue) => {
    setAiSummary(newValue);
    emitChange({ accuracy, aiSummary: newValue });
  };

  useEffect(() => {
    if (!value) {
      setAccuracy(DEFAULT_AI_SEARCH_ACCURACY);
      setAiSummary('');
      return;
    }

    setAccuracy(value.accuracy ?? DEFAULT_AI_SEARCH_ACCURACY);
    setAiSummary(value.aiSummary || '');
  }, [value]);

  return (
    <Box className={classes.aiSearchContainer}>
      <TextBox
        label={title}
        name={name}
        placeholder={placeholder}
        value={value?.aiSummary ?? aiSummary}
        multiline
        rows={3}
        fullWidth
        noMargin
        variant="outlined"
        size="small"
        onChange={handleAiSummaryChange}
      />
      <When condition={showAccuracySettings}>
        <Box className={classes.buttonsWrapper}>
          <Tooltip title={strings.shared.ui.settings}>
            <IconButton size="small" onClick={handleOpenSettings}>
              <TuneIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          onClose={handleCloseSettings}
          open={isSettingsOpen}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box minWidth={300} px={3} py={2}>
            <Box alignItems="center" display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body1">{strings.shared.ui.accuracy}</Typography>
              <Typography variant="body1">{accuracy.toFixed(2)}</Typography>
            </Box>
            <Slider
              aria-label={strings.shared.ui.accuracy}
              marks={ACCURACY_MARKS}
              max={1}
              min={0}
              onChange={(_, newValue) => setAccuracy(newValue)}
              onChangeCommitted={(_, newValue) => handleOnMouseUpSlider(newValue)}
              step={0.01}
              value={accuracy}
              valueLabelDisplay="auto"
              valueLabelFormat={val => val.toFixed(2)}
            />
          </Box>
        </Popover>
      </When>
    </Box>
  );
};
